import React, { useCallback, useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";

const RANGE_LABELS = [
    { key: "all",      label: "Tutte" },
    { key: "upcoming", label: "In corso" },
    { key: "today",    label: "Oggi" },
    { key: "overdue",  label: "Scadute" },
];

function fmtItDate(iso) {
    if (!iso) return "—";
    const d = new Date(iso + "T00:00:00");
    return d.toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" });
}

function statusFor(item) {
    if (item.is_overdue) return { key: "scaduta", label: "Scaduta" };
    if (item.days_to_due === 0) return { key: "oggi", label: "Oggi" };
    if (item.days_to_due > 0 && item.days_to_due <= 7) return { key: "soon", label: "In scadenza" };
    return { key: "scheduled", label: "Programmata" };
}

function StatusChip({ item }) {
    const s = statusFor(item);
    return <span className={`lt-chip-scad lt-chip-scad--${s.key}`}>{s.key !== "scheduled" && <span className="dot" />}{s.label}</span>;
}

function dueText(item) {
    if (item.is_overdue) {
        const d = Math.abs(item.days_to_due);
        return <span style={{ color: "#8C2C38" }}>{`Scaduta da ${d} giorn${d === 1 ? 'o' : 'i'}`}</span>;
    }
    if (item.days_to_due === 0) return <span style={{ color: "#5F6560" }}>Scade oggi</span>;
    if (item.days_to_due === 1) return <span style={{ color: "#5F6560" }}>Tra 1 giorno</span>;
    return <span style={{ color: "#5F6560" }}>{`Tra ${item.days_to_due} giorni`}</span>;
}

export default function PortalScadenze() {
    const [items, setItems] = useState([]);
    const [counters, setCounters] = useState({});
    const [rangeFilter, setRangeFilter] = useState("all");
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [list, ctr] = await Promise.all([
                api.get("/client/scadenze", { params: { range: rangeFilter, page_size: 100 } }),
                api.get("/client/scadenze/counters"),
            ]);
            setItems(list.data.items);
            setCounters(ctr.data);
        } catch {
            toast.error("Errore caricamento scadenze");
        } finally {
            setLoading(false);
        }
    }, [rangeFilter]);

    useEffect(() => { load(); }, [load]);

    const cards = [
        { key: "in_corso",        label: "In corso",              value: counters.in_corso ?? 0 },
        { key: "in_scadenza_7gg", label: "In scadenza (7 giorni)", value: counters.in_scadenza_7gg ?? 0 },
        { key: "scadute",         label: "Scadute",                value: counters.scadute ?? 0 },
        { key: "totali",          label: "Totali",                 value: counters.totali ?? 0 },
    ];

    return (
        <div style={{ padding: "32px 40px", maxWidth: 960 }} data-testid="portal-scad-page">
            <h1 className="lt-h1" style={{ marginBottom: 6 }}>Scadenze</h1>
            <p className="lt-sub" style={{ marginBottom: 24 }}>Le scadenze del tuo account ENERLEFT.</p>

            <div className="grid" style={{ gridTemplateColumns: "repeat(4, 1fr)", gap: 16, marginBottom: 20 }}>
                {cards.map((c) => (
                    <div key={c.key} className="lt-counter-card" style={{ cursor: "default" }} data-testid={`portal-scad-counter-${c.key}`}>
                        <div className="lt-counter-card__value">{c.value}</div>
                        <div className="lt-counter-card__label">{c.label}</div>
                    </div>
                ))}
            </div>

            <div className="lt-segmented" style={{ marginBottom: 18 }} data-testid="portal-scad-filter-range">
                {RANGE_LABELS.map((r) => (
                    <button key={r.key} type="button" className={"lt-segmented__btn" + (rangeFilter === r.key ? " is-active" : "")} onClick={() => setRangeFilter(r.key)}>{r.label}</button>
                ))}
            </div>

            {loading ? (
                <div style={{ color: "#8A8F88" }}>Caricamento…</div>
            ) : items.length === 0 ? (
                <div className="lt-card" style={{ padding: 32, color: "#8A8F88", textAlign: "center" }}>
                    Al momento non risultano scadenze.
                </div>
            ) : (
                <div className="flex flex-col" style={{ gap: 12 }}>
                    {items.map((it) => (
                        <div key={it.id} className="lt-panel" data-testid={`portal-scad-${it.id}`}>
                            <div className="flex items-center" style={{ gap: 12, marginBottom: 6 }}>
                                <span style={{ flex: 1, color: "#181B20", fontWeight: 600, fontSize: 15 }}>{it.title}</span>
                                <StatusChip item={it} />
                            </div>
                            <div className="flex items-center" style={{ gap: 12, fontSize: 13.5 }}>
                                <span style={{ color: "#181B20" }}>{fmtItDate(it.due_date)}</span>
                                <span style={{ color: "#E3E5E1" }}>•</span>
                                {dueText(it)}
                            </div>
                            {it.notes && (
                                <div style={{ marginTop: 10, color: "#5F6560", fontSize: 13.5, whiteSpace: "pre-wrap" }}>{it.notes}</div>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
